import { FC, useState, useEffect, useMemo } from 'react';
import { Modal } from '../components/Modal';
import { Input, Select, Button, CopyableInput } from '../components/Form';
import { SortableHeader } from '../components/SortableHeader';
import { useSorting } from '../hooks/useSorting';
import {
  useGetSubscriptionPeriodsQuery,
  useCreateSubscriptionPeriodMutation,
  useUpdateSubscriptionPeriodMutation,
  useDeleteSubscriptionPeriodMutation,
  useGetOrganizationsSelectQuery,
  useGetSubscriptionPlansSelectQuery,
  SubscriptionPeriodSummary,
  SubscriptionPeriodCreate,
  SubscriptionPeriodUpdate,
} from '../api/api';

const formatStartDate = (dateString: string): string => {
  // Create date object from the input string
  const date = new Date(dateString);

  // Convert to ISO string, take just the date part, and append the time for midnight UTC
  const midnightUTC = new Date(date.toISOString().split('T')[0] + 'T00:00:00Z');

  // Get the Unix timestamp in seconds as a string
  return Math.floor(midnightUTC.getTime() / 1000).toString();
};

const formatEndDate = (dateString: string): string => {
  // Create date object from the input string
  const date = new Date(dateString);

  // Convert to ISO string, take just the date part, and append the time for one second before midnight UTC
  const oneSecondBeforeMidnightUTC = new Date(date.toISOString().split('T')[0] + 'T23:59:59Z');

  // Get the Unix timestamp in seconds as a string
  return Math.floor(oneSecondBeforeMidnightUTC.getTime() / 1000).toString();
};

const formatDateForInput = (timestamp: string): string => {
  return new Date(parseInt(timestamp) * 1000).toISOString().split('T')[0];
};

const formatMonthYear = (timestamp: string): string => {
  const date = new Date(parseInt(timestamp) * 1000);
  const day = date.getUTCDate();
  const month = date.toLocaleString('en-US', { month: 'short', timeZone: 'UTC' });
  const year = date.getUTCFullYear();
  return `${day} ${month.toLowerCase()} ${year}`;
};

const formatPrice = (amount: number, currencySign: string): string => {
  return `${currencySign}${amount.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const SubscriptionPeriodForm: FC<{
  subscriptionPeriod?: SubscriptionPeriodSummary;
  onSubmit: (data: SubscriptionPeriodCreate | SubscriptionPeriodUpdate) => void;
  onCancel: () => void;
}> = ({ subscriptionPeriod, onSubmit, onCancel }) => {
  const { data: organizations } = useGetOrganizationsSelectQuery();
  const { data: subscriptionPlans } = useGetSubscriptionPlansSelectQuery();

  // Find initial organization and subscription plan based on period data
  const initialOrg = organizations?.find(o => o.name === subscriptionPeriod?.organization_name);
  const initialPlan = subscriptionPlans?.find(p => p.name === subscriptionPeriod?.subscription_plan_name);

  const [formData, setFormData] = useState<SubscriptionPeriodCreate>({
    subscription_plan_id: initialPlan?._id || '',
    subscription_plan_name: subscriptionPeriod?.subscription_plan_name || '',
    organization_id: initialOrg?._id || '',
    organization_name: subscriptionPeriod?.organization_name || '',
    total_excluding_tax: subscriptionPeriod?.total_excluding_tax || 0,
    period_start: subscriptionPeriod?.period_start ? formatDateForInput(subscriptionPeriod.period_start) : '',
    period_end: subscriptionPeriod?.period_end ? formatDateForInput(subscriptionPeriod.period_end) : '',
    type: 'subscription_period',
    purpose_type: subscriptionPeriod?.purpose_type || 'paid',
    project_limit: subscriptionPeriod?.project_limit || 0,
    session_limit: subscriptionPeriod?.session_limit || 0,
    payment_received: subscriptionPeriod?.payment_received || false,
  });

  // Update form data when organizations data is loaded
  useEffect(() => {
    if (subscriptionPeriod && organizations && !formData.organization_id) {
      const org = organizations.find(o => o.name === subscriptionPeriod.organization_name);
      if (org) {
        setFormData(prev => ({
          ...prev,
          organization_id: org._id,
          organization_name: org.name
        }));
      }
    }
  }, [subscriptionPeriod, organizations, formData.organization_id]);

  // Update form data when subscription plans data is loaded
  useEffect(() => {
    if (subscriptionPeriod && subscriptionPlans && !formData.subscription_plan_id) {
      const plan = subscriptionPlans.find(p => p.name === subscriptionPeriod.subscription_plan_name);
      if (plan) {
        setFormData(prev => ({
          ...prev,
          subscription_plan_id: plan._id,
          subscription_plan_name: plan.name
        }));
      }
    }
  }, [subscriptionPeriod, subscriptionPlans, formData.subscription_plan_id]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const submissionData = {
      ...formData,
      period_start: formatStartDate(formData.period_start),
      period_end: formatEndDate(formData.period_end),
    };

    if (subscriptionPeriod) {
      // For update, only include the fields that are in SubscriptionPeriodUpdate
      const updateData: SubscriptionPeriodUpdate = {
        subscription_plan_id: submissionData.subscription_plan_id,
        subscription_plan_name: submissionData.subscription_plan_name,
        total_excluding_tax: submissionData.total_excluding_tax,
        period_start: submissionData.period_start,
        period_end: submissionData.period_end,
        type: submissionData.type,
        purpose_type: submissionData.purpose_type,
        project_limit: submissionData.project_limit,
        session_limit: submissionData.session_limit,
        payment_received: submissionData.payment_received,
      };
      onSubmit(updateData);
    } else {
      // For create, use the full form data
      onSubmit(submissionData);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {subscriptionPeriod && (
        <CopyableInput
          label="ID"
          value={subscriptionPeriod.id}
        />
      )}
      <Select
        label="Organization"
        value={formData.organization_id}
        onChange={(e) => {
          const org = organizations?.find((o) => o._id === e.target.value);
          setFormData({
            ...formData,
            organization_id: e.target.value,
            organization_name: org?.name || '',
          });
        }}
        options={organizations || []}
        required
      />

      <Select
        label="Purpose Type"
        value={formData.purpose_type || ''}
        onChange={(e) => setFormData({ ...formData, purpose_type: e.target.value })}
        options={[
          { _id: 'internal', name: 'Internal' },
          { _id: 'trial', name: 'Trial' },
          { _id: 'paid', name: 'Paid' },
          { _id: 'partner', name: 'Partner' }
        ]}
        required
      />

      {formData.purpose_type === 'paid' && (
        <div className="flex items-center">
          <input
            type="checkbox"
            id="payment_received"
            checked={formData.payment_received}
            onChange={(e) => setFormData({ ...formData, payment_received: e.target.checked })}
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
          />
          <label htmlFor="payment_received" className="ml-2 block text-sm text-gray-900">
            Payment Received
          </label>
        </div>
      )}

      <Select
        label="Subscription Plan"
        value={formData.subscription_plan_id}
        onChange={(e) => {
          const plan = subscriptionPlans?.find((p) => p._id === e.target.value);
          setFormData({
            ...formData,
            subscription_plan_id: e.target.value,
            subscription_plan_name: plan?.name || '',
          });
        }}
        options={subscriptionPlans || []}
        required
      />
      <Input
        label="Total (excluding tax)"
        type="number"
        value={formData.total_excluding_tax}
        onChange={(e) =>
          setFormData({
            ...formData,
            total_excluding_tax: parseFloat(e.target.value),
          })
        }
        required
      />
      <Input
        label="Period Start"
        type="date"
        value={formData.period_start}
        onChange={(e) =>
          setFormData({ ...formData, period_start: e.target.value })
        }
        onClick={(e) => (e.target as HTMLInputElement).showPicker()}
        className="cursor-pointer"
        required
      />
      <Input
        label="Period End"
        type="date"
        value={formData.period_end}
        onChange={(e) => setFormData({ ...formData, period_end: e.target.value })}
        onClick={(e) => (e.target as HTMLInputElement).showPicker()}
        className="cursor-pointer"
        required
      />
      <Input
        label="Project Limit"
        type="number"
        value={formData.project_limit}
        onChange={(e) =>
          setFormData({
            ...formData,
            project_limit: parseInt(e.target.value),
          })
        }
        min="-1"
        required
      />
      <Input
        label="Session Limit"
        type="number"
        value={formData.session_limit}
        onChange={(e) =>
          setFormData({
            ...formData,
            session_limit: parseInt(e.target.value),
          })
        }
        min="-1"
        required
      />
      <div className="flex justify-end space-x-2">
        <Button type="button" onClick={onCancel} className="bg-gray-600">
          Cancel
        </Button>
        <Button type="submit">
          {subscriptionPeriod ? 'Update' : 'Create'}
        </Button>
      </div>
    </form>
  );
};

export const SubscriptionPeriods: FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState<SubscriptionPeriodSummary | undefined>();
  const [selectedOrg, setSelectedOrg] = useState<string>('All Organizations');
  const [selectedPlan, setSelectedPlan] = useState<string>('All Plans');
  const [selectedPurposeType, setSelectedPurposeType] = useState<string>('All Types');
  const [selectedActive, setSelectedActive] = useState<string>('Active');
  const currencySign = import.meta.env.VITE_APP_APP_STAGE === 'ru' ? '₽' : '$';

  const { data: subscriptionPeriods, isLoading } = useGetSubscriptionPeriodsQuery();
  const [createSubscriptionPeriod] = useCreateSubscriptionPeriodMutation();
  const [updateSubscriptionPeriod] = useUpdateSubscriptionPeriodMutation();
  const [deleteSubscriptionPeriod] = useDeleteSubscriptionPeriodMutation();

  // Get unique organizations and plans for filters
  const uniqueOrgs = useMemo(() => {
    if (!subscriptionPeriods) return [];
    return ['All Organizations', ...new Set(subscriptionPeriods.map(period => period.organization_name))];
  }, [subscriptionPeriods]);

  const uniquePlans = useMemo(() => {
    if (!subscriptionPeriods) return [];
    return ['All Plans', ...new Set(subscriptionPeriods.map(period => period.subscription_plan_name))];
  }, [subscriptionPeriods]);

  // Filter subscription periods
  const filteredPeriods = useMemo(() => {
    if (!subscriptionPeriods) return [];

    const nowTimestamp = Math.floor(Date.now() / 1000);

    return subscriptionPeriods.filter(period => {
      const matchesOrg = selectedOrg === 'All Organizations' || period.organization_name === selectedOrg;
      const matchesPlan = selectedPlan === 'All Plans' || period.subscription_plan_name === selectedPlan;
      const matchesPurposeType = selectedPurposeType === 'All Types' || selectedPurposeType === period.purpose_type

      const periodStart = parseInt(period.period_start);
      const periodEnd = parseInt(period.period_end);
      const isActive = nowTimestamp >= periodStart && nowTimestamp <= periodEnd;

      const matchesActive = selectedActive === 'All Periods' ||
        (selectedActive === 'Active' && isActive) ||
        (selectedActive === 'Inactive' && !isActive);

      return matchesOrg && matchesPlan && matchesPurposeType && matchesActive;
    });
  }, [subscriptionPeriods, selectedOrg, selectedPlan, selectedPurposeType, selectedActive]);

  const { items: sortedPeriods, requestSort, sortConfig } = useSorting<SubscriptionPeriodSummary>(filteredPeriods);

  const handleSubmit = async (data: SubscriptionPeriodCreate | SubscriptionPeriodUpdate) => {
    try {
      if (selectedPeriod) {
        await updateSubscriptionPeriod({
          id: selectedPeriod.id,
          body: data as SubscriptionPeriodUpdate,
        });
      } else {
        await createSubscriptionPeriod(data as SubscriptionPeriodCreate);
      }
      setIsModalOpen(false);
      setSelectedPeriod(undefined);
    } catch (error) {
      console.error('Error saving subscription period:', error);
    }
  };

  const handleDelete = async () => {
    if (selectedPeriod && window.confirm('Are you sure you want to delete this subscription period?')) {
      try {
        await deleteSubscriptionPeriod(selectedPeriod.id);
        setIsModalOpen(false);
        setSelectedPeriod(undefined);
      } catch (error) {
        console.error('Error deleting subscription period:', error);
      }
    }
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold">Subscription Periods</h1>
        <Button onClick={() => setIsModalOpen(true)}>Create Subscription Period</Button>
      </div>

      <div className="mb-6 space-y-4">
        <div className="flex space-x-4">
          <div className="flex-1">
            <Select
              label="Organization"
              value={selectedOrg}
              onChange={(e) => setSelectedOrg(e.target.value)}
              options={uniqueOrgs.map(org => ({ _id: org, name: org }))}
              showEmptyOption={false}
            />
          </div>
          <div className="w-64">
            <Select
              label="Plan"
              value={selectedPlan}
              onChange={(e) => setSelectedPlan(e.target.value)}
              options={uniquePlans.map(plan => ({ _id: plan, name: plan }))}
              showEmptyOption={false}
            />
          </div>
          <div className="w-64">
            <Select
              label="Purpose Type"
              value={selectedPurposeType}
              onChange={(e) => setSelectedPurposeType(e.target.value)}
              options={[
                { _id: 'All Types', name: 'All Types' },
                { _id: 'paid', name: 'Paid' },
                { _id: 'partner', name: 'Partner' },
                { _id: 'internal', name: 'Internal' },
                { _id: 'trial', name: 'Trial' }
              ]}
              showEmptyOption={false}
            />
          </div>
          <div className="w-64">
            <Select
              label="Period"
              value={selectedActive}
              onChange={(e) => setSelectedActive(e.target.value)}
              options={[
                { _id: 'All Periods', name: 'All Periods' },
                { _id: 'Active', name: 'Active' },
                { _id: 'Inactive', name: 'Inactive' }
              ]}
              showEmptyOption={false}
            />
          </div>
        </div>
      </div>

      <div className="bg-white shadow-md rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <SortableHeader<SubscriptionPeriodSummary>
                label="Organization"
                sortKey="organization_name"
                currentSort={sortConfig}
                onSort={requestSort}
              />
              <SortableHeader<SubscriptionPeriodSummary>
                label="Plan"
                sortKey="subscription_plan_name"
                currentSort={sortConfig}
                onSort={requestSort}
              />
              <SortableHeader<SubscriptionPeriodSummary>
                label="Period"
                sortKey="period_start"
                currentSort={sortConfig}
                onSort={requestSort}
              />
              <SortableHeader<SubscriptionPeriodSummary>
                label="Price"
                sortKey="total_excluding_tax"
                currentSort={sortConfig}
                onSort={requestSort}
              />
              <SortableHeader<SubscriptionPeriodSummary>
                label="Purpose Type"
                sortKey="purpose_type"
                currentSort={sortConfig}
                onSort={requestSort}
              />
              <SortableHeader<SubscriptionPeriodSummary>
                label="Payment Type"
                sortKey="subtype"
                currentSort={sortConfig}
                onSort={requestSort}
              />
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {sortedPeriods?.map((period) => (
              <tr
                key={period.id}
                onClick={() => {
                  setSelectedPeriod(period);
                  setIsModalOpen(true);
                }}
                className="cursor-pointer hover:bg-gray-50"
              >
                <td className="px-6 py-4 whitespace-nowrap">
                  {period.organization_name}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {period.subscription_plan_name}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {formatMonthYear(period.period_start)} - {formatMonthYear(period.period_end)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {formatPrice(period.total_excluding_tax, currencySign)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full 
                    ${period.purpose_type === 'paid' ? 'bg-green-100 text-green-800' :
                      period.purpose_type === 'trial' ? 'bg-blue-100 text-blue-800' :
                        period.purpose_type === 'internal' ? 'bg-gray-100 text-gray-800' :
                          'bg-purple-100 text-purple-800'}`}>
                    {period.purpose_type}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="ml-2 px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                    {period.subtype.toUpperCase()}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedPeriod(undefined);
        }}
        title={selectedPeriod ? 'Edit Subscription Period' : 'Create Subscription Period'}
      >
        <div className="space-y-4">
          <SubscriptionPeriodForm
            subscriptionPeriod={selectedPeriod}
            onSubmit={handleSubmit}
            onCancel={() => {
              setIsModalOpen(false);
              setSelectedPeriod(undefined);
            }}
          />
          {selectedPeriod && (
            <div className="flex justify-end">
              <Button
                onClick={handleDelete}
                className="bg-red-600 hover:bg-red-700"
              >
                Delete Subscription Period
              </Button>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
}; 
